import useSWR, { SWRResponse } from 'swr'
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined'
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined'
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined'
import { useMemo } from 'react'
import { Typography, Stack, Grid } from '@mui/material'
import Loading from '../../../components/Loading'
import DashboardDivider from '../components/DashboardDivider'
import DashboardMainInfoItem from '../components/DashboardMainInfoItem'
import DashboardOrdersTable from '../../Orders/table/DashboardOrdersTable'
import axiosInstance from '../../../utils/axiosInstance'
import LeadFunnelProgressItem, { RenderLeadIcon } from '../components/LeadFunnelProgressItem'

const ResellerDashboard = () => {
  const { data: ordersData, isLoading }: SWRResponse = useSWR(
    '/orders?orderBy=createdAt&orderDirection=desc&include=metaCampaign&rowsPerPage=10',
    axiosInstance
  )
  const orders = ordersData?.data?.orders || []

  const { data: leadsData }: SWRResponse = useSWR(
    '/leads?orderBy=createdAt&orderDirection=desc',
    axiosInstance
  )

  const { data: customerDashBoard, isLoading: customerDashBoardLoading }: SWRResponse = useSWR(
    '/customerDashboard',
    axiosInstance
  )

  const { data: customersData }: SWRResponse = useSWR('/customers', (url) => axiosInstance.get(url))

  const leads = useMemo(() => {
    return leadsData ? [...leadsData.data] : []
  }, [leadsData])

  const leadsStatistics = useMemo(() => {
    const STATUS_LABELS: Record<string, string> = {
      new: 'New',
      inProgress: 'In progress',
      contacted: 'Contacted',
      hired: 'Hired',
      rejected: 'Rejected',
      spam: 'Spam',
      synced: 'Synced',
    }

    if (!leads?.length) {
      return Object.entries(STATUS_LABELS).map(([key, label]) => ({
        key,
        label,
        count: 0,
        percent: 0,
      }))
    }

    const statuses = Object.keys(STATUS_LABELS)

    const counts = statuses.map((status) => {
      return leads.filter((lead) => lead.status === status).length
    })

    const total = leads.length

    const rawPercents = counts.map((count) => (count / total) * 100)
    const roundedPercents = rawPercents.map((p) => Math.floor(p))

    let diff = 100 - roundedPercents.reduce((a, b) => a + b, 0)
    let i = 0
    while (diff > 0) {
      roundedPercents[i % statuses.length] += 1
      diff -= 1
      i += 1
    }

    return statuses.map((status, index) => ({
      key: status,
      label: STATUS_LABELS[status],
      count: counts[index],
      percent: roundedPercents[index],
    }))
  }, [leads])

  if (isLoading) {
    return <Loading />
  }

  return (
    <Stack mb={4} p={1} spacing={5}>
      <Stack p={2.5} borderRadius={4} spacing={4} sx={{ backgroundColor: '#fff' }}>
        <Typography fontSize={20} fontWeight={600} color="#1F0E72">
          Dashboard
        </Typography>
        <Grid
          container
          alignItems="center"
          sx={{
            gap: { xs: 2, md: 0 },
          }}
        >
          <Grid item sm={3.5} xs={12}>
            <DashboardMainInfoItem
              isLoading={customerDashBoardLoading}
              title="Customer"
              value={customersData?.data?.customers.length || 0}
              Icon={<PeopleOutlineOutlinedIcon htmlColor="#fff" fontSize="large" />}
            />
          </Grid>
          <Grid
            item
            xs={0.75}
            sx={{
              display: { xs: 'none', sm: 'block' },
            }}
          >
            <DashboardDivider />
          </Grid>
          <Grid item sm={3.5} xs={12}>
            <DashboardMainInfoItem
              isLoading={customerDashBoardLoading}
              title="Campaigns"
              value={ordersData.data.totalCount}
              Icon={<CampaignOutlinedIcon htmlColor="#fff" fontSize="large" />}
            />
          </Grid>
          <Grid
            item
            xs={0.75}
            sx={{
              display: { xs: 'none', sm: 'block' },
            }}
          >
            <DashboardDivider />
          </Grid>
          <Grid item sm={3.5} xs={12}>
            <DashboardMainInfoItem
              isLoading={customerDashBoardLoading}
              title="Total Leads"
              value={customerDashBoard?.data?.leadsCount || 0}
              Icon={<HowToRegOutlinedIcon htmlColor="#fff" fontSize="large" />}
            />
          </Grid>
        </Grid>
      </Stack>
      <Stack width="100%" p={2.5} borderRadius={4} sx={{ backgroundColor: '#fff' }}>
        <Grid container spacing={10}>
          <Grid item md={8} xs={12}>
            <Stack spacing={1}>
              <Typography fontSize={20} fontWeight={600} color="#1F0E72">
                Recent Orders
              </Typography>
              <Typography fontSize={14} color="#757575">
                Last 10 campaign orders
              </Typography>
            </Stack>
            <DashboardOrdersTable orders={orders} />
          </Grid>
          <Grid item md={4} xs={12}>
            <Typography fontSize={20} fontWeight={600} color="#1F0E72">
              Lead Funnel
            </Typography>
            <Stack pt={5} spacing={window.innerWidth < 600 ? 2 : 9}>
              {leadsStatistics.map((lead) => (
                <LeadFunnelProgressItem
                  color="#1F0E72"
                  Icon={<RenderLeadIcon leadKey={lead.key} color="#1F0E72" />}
                  title={lead.label}
                  percentage={lead.percent}
                  value={lead.count}
                  key={lead.key}
                />
              ))}
            </Stack>
          </Grid>
        </Grid>
      </Stack>
    </Stack>
  )
}

export default ResellerDashboard
